import type { Qubic } from "api";
import cn from "classnames";
import { cubeUrl } from "constants/products.constants";
import { formatCount, usagePercent } from "utils/number.utils";
import type { OwnedUsage, UsageAxis } from "./store.model";
import { requestAccessUrl } from "./store.model";

const BADGE_LABEL = { popular: "Popular", new: "New" } as const;

type UseItemProps = {
  axis: UsageAxis;
  /** Read under the figure when there is a cap to measure against. */
  label: string;
  /** Read instead when there is none: the count is only what was used. */
  openLabel: string;
};

const UseItem = ({ axis, label, openLabel }: UseItemProps) => (
  <div className="st-use__i">
    <b className="st-use__n">
      {axis.limit === null ? formatCount(axis.used) : `${formatCount(axis.used)} / ${formatCount(axis.limit)}`}
    </b>
    <span className="st-use__l">{axis.limit === null ? openLabel : label}</span>
    {axis.limit !== null && (
      <span className="st-use__bar">
        <span className="st-use__tr">
          <i style={{ width: `${usagePercent(axis.percent)}%` }} />
        </span>
        <em>{usagePercent(axis.percent)}%</em>
      </span>
    )}
  </div>
);

type StoreCardProps = {
  qubic: Qubic;
  /** Null for a card the company does not own, or owns with no row to read. */
  usage: OwnedUsage | null;
  onManage: () => void;
};

/**
 * One `.st-c`. A product the company holds shows where to manage it and what it
 * has used; one it does not hold sells itself — or says it is not on sale yet.
 */
export const StoreCard = ({ qubic, usage, onManage }: StoreCardProps) => {
  const { owned } = qubic;

  return (
    <article className="st-c">
      <div className="st-c__top">
        <img
          className={cn("pccube", { "pccube--own pccube--ice": owned })}
          src={cubeUrl(qubic.cube)}
          alt=""
          width={50}
          height={50}
        />
        {owned ? (
          <span className="pc-tag pc-tag--ok">Owned</span>
        ) : (
          qubic.badge !== null && <span className="pc-tag pc-tag--ok">{BADGE_LABEL[qubic.badge]}</span>
        )}
      </div>

      <div className="st-c__h">
        {qubic.url === null ? (
          qubic.name
        ) : (
          <a href={qubic.url} target="_blank" rel="noopener noreferrer">
            {qubic.name}
          </a>
        )}
      </div>

      {!owned && <p className="st-c__t">{qubic.blurb}</p>}

      {owned && (
        <div className="st-own">
          <a
            className="st-own__go"
            href="#"
            onClick={(event) => {
              event.preventDefault();
              onManage();
            }}
          >
            Manage in My Qubics <span>&#8594;</span>
          </a>
        </div>
      )}

      {owned && usage !== null && (
        <div className={cn("st-use", { "st-use--one": usage.unique === null, "st-use--out": usage.out })}>
          <UseItem axis={usage.calls} label="Calls per month" openLabel="Calls this month" />
          {usage.unique !== null && (
            <UseItem axis={usage.unique} label="Unique shipments" openLabel="Unique shipments this month" />
          )}
        </div>
      )}

      {/* Documentation stays out until the catalogue has a `docs_url` to link to. */}
      {qubic.docs_url !== null || !owned ? (
        <div className="st-c__acts">
          {qubic.docs_url !== null && (
            <a className="st-c__doc" href={qubic.docs_url} target="_blank" rel="noopener noreferrer">
              Documentation
            </a>
          )}
          {!owned &&
            (qubic.availability === "coming_soon" ? (
              <span className="st-c__soon">Coming soon</span>
            ) : (
              <a className="pcbtn pcbtn--main" href={requestAccessUrl(qubic)} target="_blank" rel="noopener noreferrer">
                Request access
              </a>
            ))}
        </div>
      ) : null}
    </article>
  );
};
